/**
 * Shared Google Ads performance-metric formatters.
 *
 * Extracted from the internal operator console (GoogleAdsOpsPage.tsx) so the
 * client-facing read-only performance page can render identical figures. Keep
 * these behaviourally identical to the operator page's local copies.
 *
 * Money is micros throughout (1 USD = 1,000,000 micros). Rates (ctr,
 * conversion_rate) are fractions, not percentages: 0.05 means 5%.
 */
import type { AdsMetricsWindow } from '@/services/googleAdsCampaignsService';

/** What a cell shows when the value is missing / unreadable. */
export const ADS_METRIC_UNKNOWN = '—';

export function adsMetricCount(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return ADS_METRIC_UNKNOWN;
  return Math.round(value).toLocaleString();
}

export function adsMetricConversions(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return ADS_METRIC_UNKNOWN;
  // Google reports fractional conversions (view-through weighting); rounding
  // 0.5 leads to "0" would hide the only lead a new ad group has had.
  return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1);
}

/** Fraction (0.05) → percentage string ("5.0%"). */
export function adsMetricRate(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return ADS_METRIC_UNKNOWN;
  return `${(value * 100).toFixed(1)}%`;
}

export function adsMetricMoney(micros: number | null | undefined): string {
  if (typeof micros !== 'number' || !Number.isFinite(micros)) return ADS_METRIC_UNKNOWN;
  const usd = micros / 1_000_000;
  // Whole dollars past $1,000 — cents on a four-figure spend is noise.
  return usd >= 1000
    ? `$${Math.round(usd).toLocaleString()}`
    : `$${usd.toFixed(2)}`;
}

export function adsMetricsWindowLabel(dateRange: string | null | undefined): string {
  if (!dateRange) return 'Performance';
  const match = dateRange.match(/^LAST_(\d+)_DAYS$/);
  if (match) return `Last ${match[1]} days`;
  return dateRange.replace(/_/g, ' ').toLowerCase();
}

/**
 * What to show instead of numbers. A failed metrics read says so (with the
 * backend's reason) rather than silently rendering zeros — "$0 spend" and
 * "we couldn't ask Google" are very different answers to "how is this doing".
 */
export function adsMetricsUnavailableLabel(
  metricsWindow: AdsMetricsWindow | null | undefined,
): string {
  if (!metricsWindow) return 'Performance not loaded yet.';
  if (metricsWindow.status === 'error') {
    return `Performance unavailable: ${metricsWindow.error || 'Google Ads metrics read failed.'}`;
  }
  if (metricsWindow.status === 'skipped') return 'Performance was not requested.';
  return 'No performance reported for this campaign.';
}

/**
 * "How long ago", compact: `just now`, `4m ago`, `3h ago`, `6d ago`.
 *
 * Past ~30 days it returns an absolute date, because "94d ago" is harder to
 * read than the date. Returns null for a missing or unparseable value so the
 * caller renders nothing rather than "Invalid Date".
 *
 * `now` is injectable for tests.
 */
export function adsRelativeAge(
  iso: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;

  const seconds = Math.floor((now.getTime() - then) / 1000);
  // Clock skew can stamp a write slightly in the future; "in 3 seconds" reads
  // as a bug.
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days <= 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Whole days since `iso`, floored, or null if missing/unparseable. */
export function adsDaysSince(
  iso: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((now.getTime() - then) / 86_400_000);
}
